import { Button, Select, Space, Switch, Tooltip, message } from "antd";
import { ModsPanelCompProps } from "../ModsPanel";
import { IAssetChangeMod } from "@blings/blings-player";
import { DynamicValueMod, DynamicValueModTypes } from "../DynamicValueMod";
import { useMst } from "../../../stores/main";
import { InfoTooltip } from "../../../BaseComps/InfoTooltip";
import { ToggleButton } from "../../../BaseComps/ToggleButton";
import Icon, { CopyOutlined, EyeOutlined } from "@ant-design/icons";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as layerSelectIcon } from "../../../icons-and-animations/selectarrow.svg";
import { resolveAssetDetails } from "../../../helpers/layerInspectorPrefill";
import { LayerInspectorSelection } from "../../../helpers/layerInspectorTools";
import { buildLayerInspectorSelectionKey } from "../../../helpers/layerSelectionUtils";
import { truncateMiddle } from "../../../utils/string";
import "./ModForm.scss";
const { Option, OptGroup } = Select;

type MediaType = "image" | "video" | "audio";

type MediaOption = {
  key: string;
  value: string;
  displayName: string;
  url: string;
  type: MediaType;
};

const MEDIA_GROUP_LABELS: Record<MediaType, string> = {
  image: "Images",
  video: "Videos",
  audio: "Audio",
};

const MEDIA_GROUP_ORDER: MediaType[] = ["image", "video", "audio"];

const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "webm", "m4v"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "aac", "m4a", "ogg"]);

const getFileExtension = (fullPath?: string): string => {
  if (!fullPath) return "";
  const [pathWithoutQuery] = fullPath.split("?");
  const segments = pathWithoutQuery.split(".");
  if (segments.length <= 1) return "";
  return segments[segments.length - 1].toLowerCase();
};

const inferMediaType = (assetName?: string): MediaType => {
  const ext = getFileExtension(assetName);
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (AUDIO_EXTENSIONS.has(ext)) return "audio";
  return "image";
};

const extractFileName = (value?: string): string | undefined => {
  if (!value) return undefined;
  const [pathWithoutQuery] = value.split("?");
  const segments = pathWithoutQuery.split("/");
  const candidate = segments[segments.length - 1];
  if (candidate) return candidate;
  return undefined;
};

const buildGroupedMediaOptions = (
  assetNames: string[],
  formatLabel: (value?: string) => string
): Record<MediaType, MediaOption[]> => {
  const grouped: Record<MediaType, MediaOption[]> = {
    image: [],
    video: [],
    audio: [],
  };
  assetNames.forEach((assetName, assetIndex) => {
    const type = inferMediaType(assetName);
    grouped[type].push({
      key: `${assetName}-${assetIndex}`,
      value: assetName,
      displayName: formatLabel(assetName),
      url: assetName,
      type,
    });
  });
  return grouped;
};

type InspectorMediaSelectionArgs = {
  enabled: boolean;
  available: boolean;
  selection?: LayerInspectorSelection;
  jsonFile?: any;
  setLayerInspectorEnabled: (
    value: boolean,
    options?: { clearSelections?: boolean }
  ) => void;
  onAssetSelected: (assetName: string) => void;
};

function useInspectorMediaSelection({
  enabled,
  available,
  selection,
  jsonFile,
  setLayerInspectorEnabled,
  onAssetSelected,
}: InspectorMediaSelectionArgs) {
  const [isCapturing, setIsCapturing] = useState(false);
  const lastSelectionKeyRef = useRef<string>("");
  const canSelect = available && Boolean(jsonFile);

  const stopCapture = useCallback(() => {
    setIsCapturing(false);
  }, []);

  const startCapture = useCallback(() => {
    if (!canSelect) return;
    lastSelectionKeyRef.current = buildLayerInspectorSelectionKey(selection);
    setIsCapturing(true);
    if (!enabled) {
      setLayerInspectorEnabled(true);
    }
  }, [canSelect, enabled, selection, setLayerInspectorEnabled]);

  useEffect(() => {
    if (!isCapturing || !jsonFile) return;
    const currentSelectionKey = buildLayerInspectorSelectionKey(selection);
    if (!selection || lastSelectionKeyRef.current === currentSelectionKey) {
      return;
    }
    const assetName = resolveAssetDetails(selection, jsonFile)?.imageName;
    if (!assetName) return;
    onAssetSelected(assetName);
    lastSelectionKeyRef.current = currentSelectionKey;
    stopCapture();
  }, [isCapturing, jsonFile, onAssetSelected, selection, stopCapture]);

  useEffect(() => {
    if (!canSelect && isCapturing) {
      stopCapture();
    }
  }, [canSelect, isCapturing, stopCapture]);

  useEffect(() => {
    return () => {
      if (isCapturing) stopCapture();
    };
  }, [isCapturing, stopCapture]);

  return {
    isCapturing,
    canSelect,
    startCapture,
    stopCapture,
  };
}

function PAR(props) {
  return (
    <Space className="PAR">
      <div>
        <Select
          popupClassName={"mod-form-select-dropdown"}
          onChange={(value) => {
            props.onChange(props.index, "slice", value !== "meet");
          }}
          value={props.slice ? "slice" : "meet"}
        >
          <Option key="slice" value="slice">
            Slice
          </Option>
          <Option key="meet" value="meet">
            Fit
          </Option>
        </Select>
        <InfoTooltip
          style={{ marginLeft: "12px", marginRight: "4px" }}
          info={
            "Dynamic images with ratio different than the current image, will either fit into the container (leaving empty places), or sliced down "
          }
        />
      </div>
      <Space>
        Align:
        <Select
          style={{ width: 120 }}
          onChange={(value) => props.onChange(props.index, "align", value)}
          onClick={(event) => event.preventDefault()}
          value={props.align}
        >
          <Option key="xMinYMin" value="xMinYMin" title="xMinYMin">
            top left
          </Option>
          <Option key="xMinYMid" value="xMinYMid" title="xMinYMid">
            center left
          </Option>
          <Option key="xMinYMax" value="xMinYMax" title="xMinYMax">
            bottom left
          </Option>
          <Option key="xMidYMin" value="xMidYMin" title="xMidYMin">
            top center
          </Option>
          <Option key="xMidYMid" value="xMidYMid" title="xMidYMid">
            centered
          </Option>
          <Option key="xMidYMax" value="xMidYMax" title="xMidYMax">
            bottom center
          </Option>
          <Option key="xMaxYMin" value="xMaxYMin" title="xMaxYMin">
            top right
          </Option>
          <Option key="xMaxYMid" value="xMaxYMid" title="xMaxYMid">
            center right{" "}
          </Option>
          <Option key="xMaxYMax" value="xMaxYMax" title="xMaxYMax">
            bottom right
          </Option>
        </Select>
      </Space>
    </Space>
  );
}

export function AssetChangeForm(props: ModsPanelCompProps<IAssetChangeMod>) {
  const {
    platformStore: { getOriginalNameOfAsset },
    playerStore: {
      layerInspectorAvailable,
      layerInspectorPrimarySelection,
      layerInspectorEnabled,
      setLayerInspectorEnabled,
      jsonFile,
    },
  } = useMst();

  const { change, assetNames, onChange, index } = props;
  const {
    isCapturing: mediaSelectionFromInspector,
    canSelect: canSelectMediaFromInspector,
    startCapture: startMediaSelectionCapture,
    stopCapture: stopMediaSelectionCapture,
  } = useInspectorMediaSelection({
    enabled: layerInspectorEnabled,
    available: layerInspectorAvailable,
    selection: layerInspectorPrimarySelection,
    jsonFile,
    setLayerInspectorEnabled,
    onAssetSelected: (assetName) => onChange(index, "imageName", assetName),
  });

  const formatAssetLabel = useCallback(
    (fullName?: string): string => {
      if (!fullName) return "";
      const original = getOriginalNameOfAsset(fullName);
      const sanitizedOriginal = extractFileName(original);
      const sanitizedValue = extractFileName(fullName);
      return sanitizedOriginal || sanitizedValue || original || fullName;
    },
    [getOriginalNameOfAsset]
  );

  const groupedMediaOptions = buildGroupedMediaOptions(
    assetNames,
    formatAssetLabel
  );
  const copyAssetName = useCallback((value: string) => {
    const copyWithFallback = () => {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        message.success("Copied");
      } catch {
        message.error("Copy failed");
      } finally {
        document.body.removeChild(textarea);
      }
    };
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => message.success("Copied"))
        .catch(() => {
          copyWithFallback();
        });
      return;
    }
    copyWithFallback();
  }, []);
  const renderPreviewTooltip = useCallback(
    (option: MediaOption) => {
      const previewContent =
        option.type === "image" ? (
          <img
            src={option.url}
            alt={option.displayName}
            className="asset-media-preview"
          />
        ) : option.type === "video" ? (
          <video
            src={option.url}
            controls
            playsInline
            className="asset-media-preview"
          />
        ) : (
          <audio
            src={option.url}
            controls
            className="asset-media-audio-preview"
          />
        );
      return (
        <div>
          <div className="asset-media-preview-wrapper">{previewContent}</div>
          <div className="asset-media-copy-box">
            <span className="asset-media-copy-text">
              {truncateMiddle(option.value, 25, undefined, true)}
            </span>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.preventDefault();
                copyAssetName(option.value);
              }}
            />
          </div>
        </div>
      );
    },
    [copyAssetName]
  );

  const handleLabelRender = useCallback(
    ({ label, value }: { label?: ReactNode; value?: string | number }) => {
      if (typeof label === "string" && label.trim().length) {
        return label;
      }
      if (typeof value === "string") {
        return formatAssetLabel(value);
      }
      return label;
    },
    [formatAssetLabel]
  );

  const selectedMediaType = inferMediaType(change.imageName);
  const isVidOrAudio =
    selectedMediaType === "video" || selectedMediaType === "audio";

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div>
        <div className="asset-media-select-row">
          <Select
            className="media-asset-select"
            style={{ width: "100%" }}
            placeholder={"Select media asset"}
            onChange={(value) => {
              onChange(index, "imageName", value);
            }}
            onClick={(event) => event.preventDefault()}
            value={change.imageName || undefined}
            labelRender={handleLabelRender}
            optionLabelProp="label"
            popupClassName={"mod-form-select-dropdown"}
          >
            {MEDIA_GROUP_ORDER.map((group) => {
              const options = groupedMediaOptions[group];
              if (!options.length) return null;
              return (
                <OptGroup key={group} label={MEDIA_GROUP_LABELS[group]}>
                  {options.map((option) => (
                    <Option
                      key={option.key}
                      value={option.value}
                      label={option.displayName}
                    >
                      <div className="asset-dropdown-option">
                        <span className="asset-dropdown-option-name">
                          {option.displayName}
                        </span>
                        <Tooltip
                          title={renderPreviewTooltip(option)}
                          overlayClassName="asset-media-preview-tooltip"
                          placement="right"
                        >
                          <span
                            className="asset-media-preview-icon"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => event.preventDefault()}
                          >
                            <EyeOutlined />
                          </span>
                        </Tooltip>
                      </div>
                    </Option>
                  ))}
                </OptGroup>
              );
            })}
          </Select>
          <ToggleButton
            size="small"
            className="asset-select-toggle"
            isOn={mediaSelectionFromInspector}
            onToggle={(nextState) => {
              if (nextState) startMediaSelectionCapture();
              else stopMediaSelectionCapture();
            }}
            disabled={!canSelectMediaFromInspector}
            icon={
              <Icon
                component={layerSelectIcon}
                style={{ fontSize: 13, display: "block" }}
              />
            }
            aria-label="Select media from player"
            title="Select media from player"
          />
        </div>
      </div>
      <DynamicValueMod
        change={change}
        onChange={onChange}
        index={index}
        modId={props.modId}
        type={DynamicValueModTypes.media}
        assetName={change.imageName}
      />
      {isVidOrAudio && (
        <>
          <div style={{ display: "flex", marginTop: "1rem" }}>
            <p>Scene length to the video length:</p>
            <Switch
              checked={change.changeSceneDurationToMediaDuration as boolean}
              onChange={(value) => {
                onChange(index, "changeSceneDurationToMediaDuration", value);
              }}
            />
            <InfoTooltip
              info={
                "If checked, the scene length will be changed to the video length"
              }
            />
          </div>
        </>
      )}
      {!isVidOrAudio && change.imageName && (
        <PAR
          onChange={onChange}
          index={index}
          change={change}
          slice={change.slice}
          align={change.align}
        />
      )}
    </Space>
  );
}
